import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '@/components/admin/ui';
import {
  ChartCard,
  EmptyState,
  RangePicker,
} from '@/components';
import type {
  AppVersionMetricsResponse,
  BreakdownEntry,
  CountryMetricsResponse,
  PaywallFunnelDefinition,
  PaywallFunnelsResponse,
  Range,
} from '@/lib/admin-types';
import { PRIMARY_HEX } from '@/lib/colors';
import { formatInt } from '@/lib/format';
import { apiJson } from '@/lib/api';

type MetricsSearch = { range: Range };

const METRICS_HINTS = {
  appVersions:
    'Distinct devices (installation_id) per Pacific calendar day, grouped by client-reported app_version on learning events (resource taps, flashcard views/completions) and game runs. Top 10 versions in the window get their own line; remaining versions roll into “other”.',
  latestDistribution:
    'Share of devices by the app_version on their most recent client event in the selected window (resource taps, flashcard views/completions, and game runs). Versions below 3% of devices are grouped into “other”.',
  countries:
    'Distinct devices per Pacific calendar day, grouped by country inferred from the device timezone (set on register). Top 10 countries in the window get their own line; remaining countries roll into “other”. Unmapped or missing timezones show as “unknown”.',
  paywallFunnels:
    'Sequential paywall funnels from Amplitude (unique users per step). Locked content uses Learning Hub taps on Reduplication; sticker/crossword gates share the subscription gate modal event.',
} as const;

/** Pie slices below this share of devices are rolled into “other”. */
const PIE_MIN_SLICE_PERCENT = 3;

const VERSION_COLORS = [
  PRIMARY_HEX,
  '#0ea5e9',
  '#f97316',
  '#8b5cf6',
  '#ec4899',
  '#14b8a6',
  '#eab308',
  '#64748b',
  '#ef4444',
  '#22c55e',
  '#78716c',
];

function fmtDay(iso: string) {
  const [, m, day] = iso.split('-');
  return `${m}/${day}`;
}

function versionLabel(key: string) {
  return key === 'unknown' ? 'Unknown' : key === 'other' ? 'Other' : key;
}

function countryLabel(key: string) {
  return key === 'unknown' ? 'Unknown' : key === 'other' ? 'Other' : key;
}

function chartTooltipFormatter(value: unknown, name: unknown) {
  const v = typeof value === 'number' ? formatInt(value) : String(value);
  return [v, versionLabel(String(name))] as [string, string];
}

function countryTooltipFormatter(value: unknown, name: unknown) {
  const v = typeof value === 'number' ? formatInt(value) : String(value);
  return [v, countryLabel(String(name))] as [string, string];
}

type PieSlice = { name: string; value: number };

function bucketPieSlices(entries: BreakdownEntry[], minPercent: number): PieSlice[] {
  const total = entries.reduce((sum, entry) => sum + entry.count, 0);
  if (total === 0) return [];

  const main: PieSlice[] = [];
  let otherCount = 0;

  for (const entry of entries) {
    const pct = (entry.count / total) * 100;
    if (entry.key === 'other' || pct < minPercent) {
      otherCount += entry.count;
    } else {
      main.push({ name: entry.key, value: entry.count });
    }
  }

  if (otherCount > 0) {
    main.push({ name: 'other', value: otherCount });
  }

  return main.sort((a, b) => b.value - a.value);
}

function formatPiePercent(count: number, total: number): string {
  if (total <= 0) return '0%';
  const pct = (count / total) * 100;
  return pct >= 10 ? `${Math.round(pct)}%` : `${pct.toFixed(1)}%`;
}

function formatStepRate(count: number, topCount: number): string {
  if (topCount <= 0) return '—';
  return formatPiePercent(count, topCount);
}

function formatFunnelAxisPct(value: number): string {
  if (value >= 10) return `${Math.round(value)}%`;
  if (value <= 0) return '0%';
  return `${value.toFixed(1)}%`;
}

type FunnelBarRow = {
  key: string;
  label: string;
  count: number;
  solid: number;
  dropoff: number;
  overallPctLabel: string;
  priorRate: string | null;
};

const FUNNEL_CHART_HEIGHT = 380;

function buildFunnelBarRows(
  steps: PaywallFunnelDefinition['steps'],
  topCount: number
): FunnelBarRow[] {
  return steps.map((step, idx) => {
    const prevCount = idx === 0 ? step.count : steps[idx - 1]?.count ?? 0;
    const solid =
      topCount > 0 ? Math.min(100, Math.max(0, (step.count / topCount) * 100)) : 0;
    const prevSolid =
      idx === 0
        ? 100
        : topCount > 0
          ? Math.min(100, Math.max(0, (prevCount / topCount) * 100))
          : 0;
    const dropoff = idx === 0 ? 0 : Math.max(0, prevSolid - solid);
    return {
      key: step.key,
      label: step.label,
      count: step.count,
      solid,
      dropoff,
      overallPctLabel: formatFunnelAxisPct(solid),
      priorRate: idx === 0 ? null : formatStepRate(step.count, prevCount),
    };
  });
}

function FunnelStepLabel(props: {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  payload?: FunnelBarRow;
}) {
  const { payload } = props;
  const x = Number(props.x);
  const y = Number(props.y);
  const width = Number(props.width);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !payload) {
    return null;
  }

  const cx = x + width / 2;
  const boxW = 76;
  const boxH = 30;
  const boxX = cx - boxW / 2;
  const boxY = y - boxH - 6;

  return (
    <g pointerEvents="none">
      <rect
        x={boxX}
        y={boxY}
        width={boxW}
        height={boxH}
        rx={4}
        fill="#ffffff"
        stroke="#e5e7eb"
      />
      <text
        x={cx}
        y={boxY + 13}
        textAnchor="middle"
        fontSize={11}
        fontWeight={600}
        fill="#111827"
      >
        {payload.overallPctLabel}
      </text>
      <text x={cx} y={boxY + 24} textAnchor="middle" fontSize={10} fill="#6b7280">
        {formatInt(payload.count)}
      </text>
    </g>
  );
}

function PaywallFunnelCard({ funnel }: { funnel: PaywallFunnelDefinition }) {
  const topCount = funnel.steps[0]?.count ?? 0;
  const lastStep = funnel.steps[funnel.steps.length - 1];
  const hasData = funnel.steps.some((step) => step.count > 0);
  const chartData = buildFunnelBarRows(funnel.steps, topCount);
  const overallConversion = formatStepRate(lastStep?.count ?? 0, topCount);
  const dropoffPatternId = `funnel-dropoff-${funnel.id}`;

  if (!hasData) {
    return (
      <ChartCard title={funnel.label} height={FUNNEL_CHART_HEIGHT}>
        <EmptyState title="No funnel data in window" />
      </ChartCard>
    );
  }

  return (
    <ChartCard title={funnel.label} height={FUNNEL_CHART_HEIGHT}>
      <div className="flex h-full flex-col">
        <div className="mb-3 shrink-0">
          <p className="text-2xl font-bold tabular-nums tracking-tight text-nova-foreground">
            {overallConversion}
          </p>
          <p className="mt-0.5 text-xs text-nova-muted-foreground">
            From{' '}
            <span className="font-medium text-nova-foreground">{funnel.steps[0]?.label}</span> to{' '}
            <span className="font-medium text-nova-foreground">{lastStep?.label}</span>
          </p>
        </div>
        <div className="min-h-0 flex-1">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 36, right: 12, left: 0, bottom: 8 }}
              barCategoryGap="22%"
            >
              <defs>
                <pattern
                  id={dropoffPatternId}
                  patternUnits="userSpaceOnUse"
                  width={8}
                  height={8}
                  patternTransform="rotate(45)"
                >
                  <rect width={8} height={8} fill="#e6f4f4" />
                  <line x1={0} y1={0} x2={0} y2={8} stroke="#9fd4d4" strokeWidth={3} />
                </pattern>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                interval={0}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                domain={[0, 100]}
                ticks={[0, 20, 40, 60, 80, 100]}
                tick={{ fontSize: 11 }}
                tickFormatter={formatFunnelAxisPct}
                axisLine={false}
                tickLine={false}
                width={44}
              />
              <Tooltip
                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const row = payload[0]?.payload as FunnelBarRow | undefined;
                  if (!row) return null;
                  return (
                    <div className="rounded-lg border border-nova-border bg-nova-card py-2 px-2.5 text-sm shadow-md">
                      <p className="font-medium text-nova-foreground">{row.label}</p>
                      <p className="text-nova-muted-foreground">{formatInt(row.count)} users</p>
                      <p className="text-nova-muted-foreground">{row.overallPctLabel} of entry</p>
                      {row.priorRate ? (
                        <p className="text-nova-muted-foreground">{row.priorRate} of prior step</p>
                      ) : null}
                    </div>
                  );
                }}
              />
              <Bar dataKey="solid" stackId="funnel" fill={PRIMARY_HEX} maxBarSize={88}>
                <LabelList content={FunnelStepLabel} />
              </Bar>
              <Bar
                dataKey="dropoff"
                stackId="funnel"
                fill={`url(#${dropoffPatternId})`}
                maxBarSize={88}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </ChartCard>
  );
}

function MetricsPage() {
  const { range } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/metrics' });
  const qc = useQueryClient();

  const metricsQ = useQuery({
    queryKey: ['admin', 'metrics', 'app-versions', range],
    queryFn: () =>
      apiJson<AppVersionMetricsResponse>(
        `/v1/admin/analytics/metrics/app-versions?range=${encodeURIComponent(range)}`
      ),
  });

  const funnelsQ = useQuery({
    queryKey: ['admin', 'metrics', 'paywall-funnels', range],
    queryFn: () =>
      apiJson<PaywallFunnelsResponse>(
        `/v1/admin/analytics/metrics/paywall-funnels?range=${encodeURIComponent(range)}`
      ),
  });

  const countriesQ = useQuery({
    queryKey: ['admin', 'metrics', 'countries', range],
    queryFn: () =>
      apiJson<CountryMetricsResponse>(
        `/v1/admin/analytics/metrics/countries?range=${encodeURIComponent(range)}`
      ),
  });

  const loading = metricsQ.isLoading;
  const funnelsLoading = funnelsQ.isLoading;
  const countriesLoading = countriesQ.isLoading;
  const data = metricsQ.data;
  const versions = data?.versions ?? [];
  const countries = countriesQ.data?.countries ?? [];

  const chartRows =
    data?.days.map((d) => ({
      label: fmtDay(d.date),
      ...d.counts,
    })) ?? [];

  const countryChartRows =
    countriesQ.data?.days.map((d) => ({
      label: fmtDay(d.date),
      ...d.counts,
    })) ?? [];

  const hasData =
    data?.days.some((d) => Object.values(d.counts).some((c) => c > 0)) ?? false;

  const hasCountryData =
    countriesQ.data?.days.some((d) => Object.values(d.counts).some((c) => c > 0)) ??
    false;

  const pieData = useMemo(
    () => bucketPieSlices(data?.latest_by_version ?? [], PIE_MIN_SLICE_PERCENT),
    [data?.latest_by_version]
  );

  const pieTotal = pieData.reduce((sum, row) => sum + row.value, 0);
  const hasPieData = pieTotal > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-nova-foreground">Metrics</h1>
          <p className="mt-1 text-sm text-nova-muted-foreground">
            App versions, countries, and subscription paywall funnels from client events
            (Pacific calendar days).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <RangePicker
            value={range}
            options={['24h', '7d', '30d', '90d']}
            onChange={(r) => {
              void navigate({ search: { range: r } });
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void qc.invalidateQueries({ queryKey: ['admin', 'metrics'] });
            }}
          >
            Refresh
          </Button>
        </div>
      </div>

      {metricsQ.error && (
        <p className="text-sm text-red-600">
          {(metricsQ.error as Error).message}
          <button
            type="button"
            className="ml-2 underline"
            onClick={() => void metricsQ.refetch()}
          >
            Retry
          </button>
        </p>
      )}

      {funnelsQ.error && (
        <p className="text-sm text-red-600">
          {(funnelsQ.error as Error).message}
          <button
            type="button"
            className="ml-2 underline"
            onClick={() => void funnelsQ.refetch()}
          >
            Retry
          </button>
        </p>
      )}

      {countriesQ.error && (
        <p className="text-sm text-red-600">
          {(countriesQ.error as Error).message}
          <button
            type="button"
            className="ml-2 underline"
            onClick={() => void countriesQ.refetch()}
          >
            Retry
          </button>
        </p>
      )}

      <ChartCard
        title="Unique devices by app version (daily)"
        tooltip={METRICS_HINTS.appVersions}
        height={320}
        loading={loading}
        error={metricsQ.error ? String(metricsQ.error) : undefined}
      >
        {!loading && !hasData ? (
          <EmptyState title="No event data in window" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis
                allowDecimals={false}
                width={44}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => formatInt(Number(v))}
              />
              <Tooltip formatter={chartTooltipFormatter} labelFormatter={(label) => `Day ${label}`} />
              <Legend formatter={(value) => versionLabel(String(value))} />
              {versions.map((ver, idx) => (
                <Line
                  key={ver}
                  type="monotone"
                  dataKey={ver}
                  name={ver}
                  stroke={VERSION_COLORS[idx % VERSION_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard
        title="Latest app version per device"
        tooltip={METRICS_HINTS.latestDistribution}
        height={320}
        loading={loading}
        error={metricsQ.error ? String(metricsQ.error) : undefined}
      >
        {!loading && !hasPieData ? (
          <EmptyState title="No event data in window" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={110}
                label={({ name, percent, value }) => {
                  const pct = (percent ?? 0) * 100;
                  if (pct < PIE_MIN_SLICE_PERCENT) return null;
                  return `${versionLabel(String(name))} (${formatPiePercent(Number(value), pieTotal)})`;
                }}
                labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
              >
                {pieData.map((entry, idx) => (
                  <Cell
                    key={entry.name}
                    fill={VERSION_COLORS[idx % VERSION_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const item = payload[0];
                  const count = Number(item.value ?? 0);
                  const name = versionLabel(String(item.name ?? item.payload?.name ?? ''));
                  return (
                    <div className="rounded-lg border border-nova-border bg-nova-card py-2 px-2.5 text-sm shadow-md">
                      <p className="font-medium text-nova-foreground">{name}</p>
                      <p className="text-nova-muted-foreground">
                        {formatInt(count)} ({formatPiePercent(count, pieTotal)})
                      </p>
                    </div>
                  );
                }}
              />
              <Legend formatter={(value) => versionLabel(String(value))} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard
        title="Unique devices by country (daily)"
        tooltip={METRICS_HINTS.countries}
        height={320}
        loading={countriesLoading}
        error={countriesQ.error ? String(countriesQ.error) : undefined}
      >
        {!countriesLoading && !hasCountryData ? (
          <EmptyState title="No event data in window" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={countryChartRows}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis
                allowDecimals={false}
                width={44}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => formatInt(Number(v))}
              />
              <Tooltip
                formatter={countryTooltipFormatter}
                labelFormatter={(label) => `Day ${label}`}
              />
              <Legend formatter={(value) => countryLabel(String(value))} />
              {countries.map((country, idx) => (
                <Line
                  key={country}
                  type="monotone"
                  dataKey={country}
                  name={country}
                  stroke={VERSION_COLORS[idx % VERSION_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <div className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-nova-foreground">Paywall funnels</h2>
          <p className="mt-1 text-sm text-nova-muted-foreground" title={METRICS_HINTS.paywallFunnels}>
            Unique Amplitude users moving from each entry point through the subscription paywall.
          </p>
        </div>
        {funnelsLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, idx) => (
              <div
                key={idx}
                className="animate-pulse rounded-lg bg-nova-muted"
                style={{ height: FUNNEL_CHART_HEIGHT }}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {(funnelsQ.data?.funnels ?? []).map((funnel) => (
              <PaywallFunnelCard key={funnel.id} funnel={funnel} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/metrics')({
  validateSearch: (raw: Record<string, unknown>): MetricsSearch => {
    const r = raw.range;
    if (r === '24h' || r === '30d' || r === '90d' || r === '7d') return { range: r };
    return { range: '7d' };
  },
  component: MetricsPage,
});
